import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";

interface DialogNode {
  _id: string;
  label?: string | null;
  interactionModuleName?: string | null;
}

interface CanonicalWidget {
  _id: string;
  slug: string;
  displayName: string;
  description?: string;
}

type ConditionKind = "always" | "expr_v1";
type DriverType = "none" | "designed_response" | "ui_component";

interface CreateFlowEdgeDialogProps {
  open: boolean;
  onCancel: () => void;
  onSave: (data: {
    fromNodeId: string;
    toNodeId?: string | null;
    conditionKind: ConditionKind;
    conditionExpression?: string;
    driverType: DriverType;
    // designed_response config
    driverAdvice?: string;
    // ui_component config
    canonicalWidgetId?: string;
    propsTemplateJson?: string;
  }) => void;
  isSubmitting: boolean;
  error?: string;
  nodes: DialogNode[];
  canonicalWidgets: CanonicalWidget[];
}

const END_OF_FLOW_VALUE = "__END__";

function getNodeDisplayLabel(node: DialogNode): string {
  return node.label || node.interactionModuleName || "(unnamed step)";
}

export function CreateFlowEdgeDialog({
  open,
  onCancel,
  onSave,
  isSubmitting,
  error,
  nodes,
  canonicalWidgets,
}: CreateFlowEdgeDialogProps) {
  // Form state
  const [fromNodeId, setFromNodeId] = useState("");
  const [toNodeId, setToNodeId] = useState("");
  const [conditionKind, setConditionKind] = useState<ConditionKind>("always");
  const [conditionExpression, setConditionExpression] = useState("");
  const [driverType, setDriverType] = useState<DriverType>("none");

  // designed_response fields
  const [driverAdvice, setDriverAdvice] = useState("");

  // ui_component fields
  const [selectedWidgetId, setSelectedWidgetId] = useState("");
  const [propsTemplateJson, setPropsTemplateJson] = useState("");
  const [jsonError, setJsonError] = useState<string | undefined>();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFromNodeId("");
      setToNodeId("");
      setConditionKind("always");
      setConditionExpression("");
      setDriverType("none");
      setDriverAdvice("");
      setSelectedWidgetId("");
      setPropsTemplateJson("");
      setJsonError(undefined);
    }
  }, [open]);

  // Validate JSON for props template
  useEffect(() => {
    if (driverType === "ui_component" && propsTemplateJson.trim()) {
      try {
        JSON.parse(propsTemplateJson);
        setJsonError(undefined);
      } catch {
        setJsonError("Invalid JSON format");
      }
    } else {
      setJsonError(undefined);
    }
  }, [driverType, propsTemplateJson]);

  const handleSubmit = () => {
    if (!fromNodeId) return;

    // Validate ui_component requirements
    if (driverType === "ui_component") {
      if (!selectedWidgetId) return;
      if (propsTemplateJson.trim()) {
        try {
          JSON.parse(propsTemplateJson);
        } catch {
          setJsonError("Invalid JSON format");
          return;
        }
      }
    }

    // Validate designed_response requirements
    if (driverType === "designed_response" && !driverAdvice.trim()) {
      return;
    }

    onSave({
      fromNodeId,
      toNodeId: toNodeId === END_OF_FLOW_VALUE ? null : toNodeId || null,
      conditionKind,
      conditionExpression:
        conditionKind === "expr_v1" ? conditionExpression : undefined,
      driverType,
      // designed_response
      driverAdvice:
        driverType === "designed_response" ? driverAdvice.trim() : undefined,
      // ui_component
      canonicalWidgetId:
        driverType === "ui_component" ? selectedWidgetId : undefined,
      propsTemplateJson:
        driverType === "ui_component" && propsTemplateJson.trim()
          ? propsTemplateJson.trim()
          : undefined,
    });
  };

  const canSubmit = (() => {
    if (!fromNodeId || isSubmitting || jsonError) return false;
    if (driverType === "designed_response" && !driverAdvice.trim())
      return false;
    if (driverType === "ui_component" && !selectedWidgetId) return false;
    return true;
  })();

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={onCancel}
        onPointerDownOutside={onCancel}
      >
        <DialogHeader>
          <DialogTitle>Add edge to flow</DialogTitle>
          <DialogDescription>
            Connect two steps with an optional condition and driver.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* From Step */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              From step <span className="text-destructive">*</span>
            </Label>
            <Select
              value={fromNodeId}
              onValueChange={setFromNodeId}
              disabled={isSubmitting}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select starting step..." />
              </SelectTrigger>
              <SelectContent>
                {nodes.map((node) => (
                  <SelectItem key={node._id} value={node._id}>
                    {getNodeDisplayLabel(node)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* To Step */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">To step</Label>
            <Select
              value={toNodeId}
              onValueChange={setToNodeId}
              disabled={isSubmitting}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select target step..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={END_OF_FLOW_VALUE}>
                  <span className="text-muted-foreground italic">
                    End of flow
                  </span>
                </SelectItem>
                {nodes.map((node) => (
                  <SelectItem key={node._id} value={node._id}>
                    {getNodeDisplayLabel(node)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Leave as "End of flow" to terminate at this edge.
            </p>
          </div>

          {/* Condition */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Condition</Label>
            <RadioGroup
              value={conditionKind}
              onValueChange={(v) => setConditionKind(v as ConditionKind)}
              disabled={isSubmitting}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="always" id="cond-always" />
                <Label htmlFor="cond-always" className="text-sm font-normal">
                  Always (no condition)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="expr_v1" id="cond-expr" />
                <Label htmlFor="cond-expr" className="text-sm font-normal">
                  Expression (expr_v1)
                </Label>
              </div>
            </RadioGroup>

            {conditionKind === "expr_v1" && (
              <div className="mt-2">
                <Textarea
                  value={conditionExpression}
                  onChange={(e) => setConditionExpression(e.target.value)}
                  placeholder="e.g., context.status === 'approved'"
                  className="text-sm h-16"
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter an expression that evaluates to true/false.
                </p>
              </div>
            )}
          </div>

          {/* Driver Type */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Flow driver</Label>
            <RadioGroup
              value={driverType}
              onValueChange={(v) => setDriverType(v as DriverType)}
              disabled={isSubmitting}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="none" id="driver-none" />
                <Label htmlFor="driver-none" className="text-sm font-normal">
                  None
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="designed_response"
                  id="driver-designed"
                />
                <Label
                  htmlFor="driver-designed"
                  className="text-sm font-normal"
                >
                  Designed response
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="ui_component" id="driver-ui" />
                <Label htmlFor="driver-ui" className="text-sm font-normal">
                  UI component
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Designed Response config */}
          {driverType === "designed_response" && (
            <div className="space-y-3 p-3 bg-muted/30 border border-border rounded-md">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Advice to LLM <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  value={driverAdvice}
                  onChange={(e) => setDriverAdvice(e.target.value)}
                  placeholder="Instructions for how the LLM should respond at this step..."
                  className="text-sm h-24"
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Tell the LLM what kind of response to generate for this
                  transition.
                </p>
              </div>
            </div>
          )}

          {/* UI Component config */}
          {driverType === "ui_component" && (
            <div className="space-y-3 p-3 bg-muted/30 border border-border rounded-md">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Widget <span className="text-destructive">*</span>
                </Label>
                {canonicalWidgets.length === 0 ? (
                  <div className="p-3 bg-muted/50 border border-border rounded-md">
                    <p className="text-sm text-muted-foreground">
                      No widgets available for this server.
                    </p>
                  </div>
                ) : (
                  <Select
                    value={selectedWidgetId}
                    onValueChange={setSelectedWidgetId}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a widget..." />
                    </SelectTrigger>
                    <SelectContent>
                      {canonicalWidgets.map((widget) => (
                        <SelectItem key={widget._id} value={widget._id}>
                          <div className="flex flex-col items-start">
                            <span className="text-sm">
                              {widget.displayName}
                            </span>
                            <span className="text-xs text-muted-foreground font-mono">
                              {widget.slug}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-muted-foreground">
                  The canonical widget to render for this driver.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Props (optional)</Label>
                <Textarea
                  value={propsTemplateJson}
                  onChange={(e) => setPropsTemplateJson(e.target.value)}
                  placeholder='{"key": "value"}'
                  className="text-sm h-20 font-mono"
                  disabled={isSubmitting}
                />
                {jsonError && (
                  <p className="text-xs text-destructive">{jsonError}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Optional JSON props to pass to the widget.
                </p>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onCancel}
            size="sm"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} size="sm" disabled={!canSubmit}>
            {isSubmitting ? "Adding..." : "Add edge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
